import React, { useContext } from 'react'
import { useRouter } from 'next/router'
import { motion } from 'framer-motion'
import { UserContext } from '../../context/UserContext'
import {
    Building2, ArrowLeft, ChevronRight, CalendarCheck,
    Dumbbell, TrendingUp
} from 'lucide-react'
import Link from 'next/link'

/**
 * Corporate Wellness hub page.
 * Shows company info header and navigation cards to sub-pages:
 *   /corporate/my-sessions   — enrolled sessions, attendance
 *   /corporate/programs      — browse programs & enroll
 *   /home/personal-training/progress — body measurement tracking
 */
export const CorporateDashboard = () => {
    const router = useRouter()
    const { corporateProfile, userId } = useContext(UserContext)

    if (!corporateProfile) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
                <Building2 size={40} className="text-gray-300 mb-4" />
                <p className="text-gray-500 text-sm text-center">You are not enrolled in any corporate wellness programs.</p>
                <button
                    onClick={() => router.push('/home')}
                    className="mt-4 px-5 py-2.5 rounded-xl text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition"
                >
                    Back to Home
                </button>
            </div>
        )
    }

    const programs = corporateProfile.programs || []

    const navItems = [
        {
            href: '/corporate/my-sessions',
            icon: CalendarCheck,
            iconBg: 'from-emerald-500 to-teal-500',
            shadowColor: 'shadow-emerald-200',
            title: 'My Sessions',
            subtitle: 'Enrolled sessions, attendance & history',
        },
        {
            href: '/corporate/programs',
            icon: Dumbbell,
            iconBg: 'from-indigo-500 to-violet-500',
            shadowColor: 'shadow-indigo-200',
            title: 'Programs',
            subtitle: `${programs.length} program${programs.length !== 1 ? 's' : ''} — browse & enroll`,
        },
    ]

    return (
        <div className="min-h-screen">
            {/* Header */}
            <div className="bg-linear-to-r from-indigo-600 via-indigo-700 to-violet-700 px-4 md:px-8 py-6 rounded-b-2xl">
                <div className="flex items-center gap-3 mb-4">
                    <button
                        onClick={() => router.push('/home')}
                        className="p-2 rounded-xl bg-white/15 hover:bg-white/25 transition"
                    >
                        <ArrowLeft size={18} className="text-white" />
                    </button>
                    <h1 className="text-lg font-bold text-white">Corporate Wellness</h1>
                </div>

                <div className="bg-white/10 rounded-2xl p-4 md:p-8 backdrop-blur-sm border border-white/10">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center">
                            <Building2 size={20} className="text-white" />
                        </div>
                        <div>
                            <p className="text-base font-bold text-white">{corporateProfile.companyName}</p>
                            <p className="text-xs text-white/60">
                                {corporateProfile.companyDomain && <span>{corporateProfile.companyDomain} · </span>}
                                {programs.length} program{programs.length !== 1 ? 's' : ''}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation Cards */}
            <div className="px-4 pt-4 pb-24 space-y-3">
                {navItems.map((item) => (
                    <Link key={item.href} href={item.href}>
                        <motion.div
                            whileTap={{ scale: 0.98 }}
                            className="flex items-center gap-3 p-3.5 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all duration-200 cursor-pointer"
                        >
                            <div className={`w-10 h-10 rounded-xl bg-linear-to-br ${item.iconBg} flex items-center justify-center shrink-0 shadow-sm ${item.shadowColor}`}>
                                <item.icon size={18} className="text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-gray-900">{item.title}</p>
                                <p className="text-[11px] text-gray-500">{item.subtitle}</p>
                            </div>
                            <ChevronRight size={16} className="text-gray-300 shrink-0" />
                        </motion.div>
                    </Link>
                ))}

                {/* Track Progress */}
                {userId && (
                    <Link href={`/home/personal-training/progress/0/${userId}`}>
                        <motion.div
                            whileTap={{ scale: 0.98 }}
                            className="flex items-center gap-3 p-3.5 bg-white rounded-2xl border border-orange-100 shadow-sm hover:shadow-md hover:border-orange-200 transition-all duration-200 cursor-pointer"
                        >
                            <div className="w-10 h-10 rounded-xl bg-linear-to-br from-orange-500 to-amber-500 flex items-center justify-center shrink-0 shadow-sm shadow-orange-200">
                                <TrendingUp size={18} className="text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-gray-900">Track Progress</p>
                                <p className="text-[11px] text-gray-500">Photos, body measurements &amp; more</p>
                            </div>
                            <ChevronRight size={16} className="text-gray-300 shrink-0" />
                        </motion.div>
                    </Link>
                )}
            </div>
        </div>
    )
}
